import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions
} from '@headlessui/react';
import { clsx } from 'clsx';
import { Check, ChevronDown } from 'lucide-react';
import { FC, useMemo } from 'react';
import { createIcon } from 'components/icons/IconFactory';
import { QuickGroupSurveyDropdownProps } from './quickGroupSurvey.types';

export const QuickGroupSurveyDropdown: FC<QuickGroupSurveyDropdownProps> = ({
  answers,
  selectedKeys,
  onSelect,
  multiple = false,
  placeholder = 'Sélectionner'
}) => {
  const selectedAnswers = useMemo(() => {
    return answers.filter(a => selectedKeys.includes(a.key));
  }, [answers, selectedKeys]);

  const displayValue = useMemo(() => {
    if (selectedAnswers.length === 0) return null;
    if (!multiple) return selectedAnswers[0];
    return selectedAnswers;
  }, [selectedAnswers, multiple]);

  const renderIcon = (iconKey: string, className?: string) => {
    const Icon = createIcon(iconKey);
    return <Icon className={clsx('w-5 h-5 text-primary', className)} />;
  };

  return (
    <Listbox
      as="div"
      value={null}
      onChange={(answer) => answer && onSelect(answer)}
      className="w-full relative"
    >
      {({ open }) => (
        <>
          <ListboxButton
            className={clsx(
              'w-full text-sm flex justify-between items-center gap-2',
              'border rounded-md px-3 py-2 bg-white cursor-pointer',
              'transition-all duration-150',
              'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1',
              !displayValue && 'text-slate-400',
              displayValue && 'text-slate-700',
              open ? 'border-primary' : 'border-slate-200 hover:border-slate-300'
            )}
          >
            <span className="truncate flex-1 text-left">
              {multiple && Array.isArray(displayValue) ? (
                <span className="flex flex-wrap gap-1">
                  {displayValue.map((answer) => (
                    <span
                      key={String(answer.key)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-medium"
                    >
                      {answer.iconKey && renderIcon(answer.iconKey, 'w-3 h-3')}
                      {answer.label}
                    </span>
                  ))}
                </span>
              ) : displayValue && !Array.isArray(displayValue) ? (
                <span className="flex items-center gap-2 font-medium">
                  {displayValue.iconKey && renderIcon(displayValue.iconKey, 'w-4 h-4')}
                  {displayValue.label}
                </span>
              ) : (
                <span>{placeholder}</span>
              )}
            </span>
            <ChevronDown
              className={clsx(
                'w-4 h-4 text-slate-400 transition-transform duration-150 flex-shrink-0',
                open && 'rotate-180 text-primary'
              )}
            />
          </ListboxButton>

          <ListboxOptions
            // 'bottom end' : le menu s'aligne sur le bord droit du champ et s'élargit vers
            // la gauche -> les options longues restent visibles, même dans un panneau latéral.
            anchor={{ to: 'bottom end', gap: 4, padding: 16 }}
            className={clsx(
              'min-w-[var(--button-width)] w-max max-w-[90vw] bg-white z-50 overflow-y-auto',
              'border border-slate-200 rounded-md shadow-lg',
              '[--anchor-max-height:50vh]'
            )}
          >
            {answers.map((answer) => {
              const isSelected = selectedKeys.includes(answer.key);
              return (
                <ListboxOption
                  key={String(answer.key)}
                  value={answer}
                  className={({ focus }) =>
                    clsx(
                      'px-3 py-2 cursor-pointer text-sm transition-colors duration-100',
                      'flex items-center justify-between gap-2',
                      'border-b border-slate-50 last:border-b-0',
                      isSelected
                        ? 'bg-primary/5 text-primary font-medium'
                        : focus
                          ? 'bg-slate-50 text-slate-700'
                          : 'text-slate-600'
                    )
                  }
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {answer.iconKey && renderIcon(answer.iconKey)}
                    <span className="whitespace-normal break-words">{answer.label}</span>
                  </span>
                  {isSelected && (
                    <Check className="w-4 h-4 flex-shrink-0 text-primary" />
                  )}
                </ListboxOption>
              );
            })}
          </ListboxOptions>
        </>
      )}
    </Listbox>
  );
};
